import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import { token } from '@atlaskit/tokens';

export const CONTRIBUTOR_TAG_TESTID = 'diff-contributor-tag';
export const CONTRIBUTOR_TAG_NAME_TESTID = 'diff-contributor-tag-name';

/**
 * Floor for the tag width (~one 16px avatar plus padding), so the highlight-width clamp cannot
 * collapse the box on a very short highlight.
 */
export const MIN_TAG_WIDTH = '28px';

// Positioned against the host widget its decoration renders on the change's first character:
// `bottom: 100%` lifts the tag onto the line above, `inset-inline-start` starts it on that
// character.
const constraintStyle = convertToInlineCss({
	display: 'block',
	position: 'absolute',
	bottom: '100%',
	insetInlineStart: 0,
	minWidth: 0,
	// The tag may cover surrounding text, so it has to paint over it. Matches `akEditorUnitZIndex`.
	zIndex: 1,
	// It renders inside the document and it is not content: dragging a selection across the change
	// must not select it.
	userSelect: 'none',
});

// `backgroundColor`, `borderBottomColor` and the visibility come from the model, and are set by the
// controller.
const tagStyle = convertToInlineCss({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
	// Never wider than the highlight; overflow is ellipsised on the name below.
	maxWidth: '100%',
	minWidth: 0,
	overflow: 'hidden',
	paddingInline: token('space.050'),
	paddingBlock: token('space.025'),
	borderRadius: token('radius.small'),
	borderBottomStyle: 'solid',
	borderBottomWidth: token('border.width.selected'),
	boxShadow: token('elevation.shadow.overlay'),
	font: token('font.body.small'),
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
});

const avatarsStyle = convertToInlineCss({
	display: 'inline-flex',
	alignItems: 'center',
	flexShrink: 0,
});

const nameStyle = convertToInlineCss({
	display: 'block',
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

/** `@atlaskit/visually-hidden`, inlined. */
const srLabelStyle = convertToInlineCss({
	width: '1px',
	height: '1px',
	padding: 0,
	position: 'absolute',
	border: 0,
	clip: 'rect(1px, 1px, 1px, 1px)',
	overflow: 'hidden',
	userSelect: 'none',
	whiteSpace: 'nowrap',
});

const createSpan = (
	doc: Document,
	style: string,
	attributes: Record<string, string> = {},
): HTMLSpanElement => {
	const span = doc.createElement('span');
	span.setAttribute('style', style);
	for (const [name, value] of Object.entries(attributes)) {
		span.setAttribute(name, value);
	}

	return span;
};

export type ContributorTagDom = {
	avatars: HTMLSpanElement;
	name: HTMLSpanElement;
	root: HTMLSpanElement;
	/**
	 * Announced after the name, which is the order design requires. `VanillaTooltip` appends its
	 * popover to the tag as well, so this is the last *content* child rather than the last node.
	 */
	srLabel: HTMLSpanElement;
	tag: HTMLSpanElement;
};

/**
 * The tag's markup, with no model applied — see `ContributorTagController`.
 *
 * Built element by element rather than through `DOMSerializer.renderSpec`, which returns `Node` and
 * would need a cast per reference below.
 */
export const buildContributorTagDom = (doc: Document): ContributorTagDom => {
	const root = createSpan(doc, constraintStyle);
	const tag = createSpan(doc, tagStyle, {
		'data-testid': CONTRIBUTOR_TAG_TESTID,
		// Deliberately no `aria-label`: it would be announced ahead of the element's contents, but
		// design requires the contributor *after* the change — hence the visually hidden label.
		role: 'note',
		// Focusable so keyboard and screen-reader users can reach the tag and its tooltip.
		tabindex: '0',
	});
	const avatars = createSpan(doc, avatarsStyle);
	const name = createSpan(doc, nameStyle, { 'data-testid': CONTRIBUTOR_TAG_NAME_TESTID });
	const srLabel = createSpan(doc, srLabelStyle);

	tag.append(avatars, name, srLabel);
	root.appendChild(tag);

	return { avatars, name, root, srLabel, tag };
};
